import { Box, Button, Flex, Text } from "@chakra-ui/react"
import { useContext } from "react"
import { Link, useNavigate } from "react-router-dom"
import Icon, { Icons } from "../base-components/Icons";
import { RoutePath, SPACING } from "./config";
import { Context } from "../App";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { useScopes } from "@/model/Token";

const Drawer = ({ ratioInPercent, onSelect }: { ratioInPercent: number; onSelect: null | ((path: string) => void) }) => {
    const { translations } = useContext(Context);
    const scope = useScopes();
    const isAdmin = scope.includes("ADMIN") || scope.includes("SUPERUSER");
    const isSuperUser = scope.includes("SUPERUSER");

    // Fills its (fixed-width, sticky) container from App.tsx. ratioInPercent is
    // kept for API compatibility but no longer drives width — a percentage width
    // clipped the labels on narrow screens.
    void ratioInPercent;
    const Wrapper = ({ children }: { children: React.ReactNode }) =>
        <Box textAlign="left" width="100%" rounded={"initial"} p={SPACING / 2}>{children}</Box>

    if (!isAdmin) {
        return (
            <Wrapper>
                <Box pt={SPACING / 2}>
                    <DrawerButton path={RoutePath.Tasks} title={translations.tasks.title} icon={Icons.Task} onClick={onSelect} />
                    <DrawerButton path={RoutePath.Orders} title={translations.orders.title} icon={Icons.Order} onClick={onSelect} />
                    <DrawerButton path={RoutePath.Worksites} title={translations.worksites.title} icon={Icons.Worksite} onClick={onSelect} />
                    <DrawerButton path={RoutePath.Customers} title={translations.customers.title} icon={Icons.Customer} onClick={onSelect} />
                </Box>
            </Wrapper>
        )
    };

    return (
        <Wrapper>
            <DrawerDivider title={translations.sidebar.adminstration} first />
            <DrawerButton path={RoutePath.Dashboard} title={translations.dashboard.title} icon={Icons.Dashboard} onClick={onSelect} />
            <DrawerButton path={RoutePath.Overview} title={translations.graph.title} icon={Icons.Chart} onClick={onSelect} />
            <DrawerButton path={RoutePath.Worksites} title={translations.worksites.title} icon={Icons.Worksite} onClick={onSelect} />
            <DrawerButton path={RoutePath.Customers} title={translations.customers.title} icon={Icons.Customer} onClick={onSelect} />
            <DrawerButton path={RoutePath.Orders} title={translations.orders.title} icon={Icons.Order} onClick={onSelect} />
            <DrawerButton path={RoutePath.Tasks} title={translations.tasks.title} icon={Icons.Task} onClick={onSelect} />

            <DrawerDivider title={translations.sidebar.configuration} />
            <DrawerButton path={RoutePath.Products} title={translations.products.title} icon={Icons.Product} onClick={onSelect} />
            <DrawerButton path={RoutePath.Resources} title={translations.resources.title} icon={Icons.Resource} onClick={onSelect} />
            <DrawerButton path={RoutePath.Properties} title={translations.properties.title} icon={Icons.Property} onClick={onSelect} />
            <DrawerButton path={RoutePath.SystemSettings} title={translations.systemSettings.title} icon={Icons.Settings} onClick={onSelect} />

            <DrawerDivider title={translations.sidebar.importExport} />
            <DrawerButton path={RoutePath.CustomerImport} title={translations.importExport.customerImport} icon={Icons.Import} onClick={onSelect} />
            <DrawerButton path={RoutePath.WorksiteImport} title={translations.importExport.worksiteImport} icon={Icons.Import} onClick={onSelect} />

            <DrawerDivider title={translations.sidebar.developer} />
            <DrawerButton path={RoutePath.TemplateEditor} title={translations.templateEditor.title} icon={Icons.Template} onClick={onSelect} />

            {isSuperUser && (
                <>
                    <DrawerDivider title={translations.sidebar.superAdmin} />
                    <DrawerButton path={RoutePath.Users} title={translations.userManagement.title} icon={Icons.UserManagement} onClick={onSelect} />
                </>
            )}
        </Wrapper>
    )
}

export default Drawer;

const DrawerButton = ({ path, title, icon, onClick, disabled = false }: { path: string; title: string; icon: IconDefinition; onClick: null | ((path: string) => void); disabled?: boolean }) => {
    const navigate = useNavigate();
    const currentPath = window.location.pathname;
    const isSelected = currentPath.includes(path);

    return (
        <Link to={path}>
            <Button
                variant="ghost"
                color={disabled ? "gray.400" : "inherit"}
                fontWeight={isSelected ? "600" : undefined}
                bg={isSelected ? "gray.200" : undefined}
                _dark={isSelected ? { bg: "gray.700" } : undefined}
                _hover={disabled ? {} : { bg: "gray.200", _dark: { bg: "gray.600" } }}
                onClick={event => {
                    // If holding ctrl/cmd, let the browser handle opening in new tab
                    if (event.ctrlKey || event.metaKey) {
                        return;
                    }

                    if (!disabled) {
                        navigate(path);
                        onClick?.(path);
                    }
                }}
                width={"100%"}
                justifyContent={"flex-start"}
                transition="all 0.25s ease"
            >
                <Flex flexGrow={1} alignItems={"center"}>
                    <Icon icon={icon} marginRight={SPACING / 2} />
                    <Text fontSize="small">{title}</Text>
                </Flex>
            </Button>
        </Link>
    )
};

const DrawerDivider = ({ title, first = false }: { title: string; first?: boolean }) => {
    return (
        <Box mt={first ? SPACING / 2 : SPACING * 1.5} mb={SPACING / 2} px={SPACING / 2}>
            <Text
                fontSize="x-small"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.08em"
                color="gray.500"
            >
                {title}
            </Text>
            <Box w="100%" h="1px" bg="gray.200" mt={SPACING / 4} _dark={{ bg: "gray.700" }} />
        </Box>
    )
};
